import dedent from 'ts-dedent';
import { ExtensionWorkflowComponent } from '../../components/common/extensioncomponent';
import { StoredProcedureWorkflowComponent } from '../../components/common/storedprocedurecomponent';
import { ReadTable } from '../../components/readtable';
import { OPTIMIZATION_COLUMN_PARAMETER_NAME } from '../../components/saveastable';
import { DRY_RUN_SUFFIX } from '../../const';
import { ColumnDataType, Language, WorkflowCodeType } from '../../types';
import { OUTPUT_TABLE_FQN_VAR_NAME, OUTPUT_TABLE_NAME_VAR_NAME, formatCodeComment, formatVariableValue, hashString } from '../../utils';
export const proceduresDataset = 'carto_workflows_custom_components';
export const EXTENSIONS_TABLE_NAME = 'WORKFLOWS_EXTENSIONS';
export class DataWarehouseConnection {
    constructor(name, tempStoragePath, type) {
        this.supportsCTEs = true;
        this.codeLanguage = Language.SQL;
        this.type = type;
        this.tempStoragePath = tempStoragePath;
        this.name = name;
    }
    getCloneTableQuery(tablename, sourceTable, codeType) {
        return this.getCreateTableQuery(tablename, `SELECT * FROM ${sourceTable}`, codeType, null);
    }
    /**
     * Returns the SQL code to compute the optimization stats for the passed table.
     * @param tablename the name of the table to compute the optimization stats for
     * @returns the SQL code to compute the optimization stats for the passed table
     */
    optimizationStatsQuery(tablename) {
        return '';
    }
    /**
     * Returns a comment that describes the component, to be included in the code of the workflow.
     * @param workflow the workflow that contains the component
     * @param componentName the name of the component
     * @returns the comment that describes the component
     */
    getComponentDescriptionAsComment(workflow, componentName) {
        const component = workflow.components[componentName];
        return formatCodeComment(this.codeLanguage, `${'='.repeat(20)} ${component.metadata.name} (v${component.getVersion(this.type)}) [${componentName}]  ${'='.repeat(20)}`);
    }
    _getProcedureBody(workflow, queries, schemas) {
        let query = '';
        for (const q of queries) {
            if (q.query) {
                let componentQuery = q.query;
                componentQuery = this.replaceVariablesInStoredProcedureQuery(workflow.variables, componentQuery, workflow.components[q.componentName].requiresDynamicSQL);
                query = dedent `${query}
          ${dedent(componentQuery)}`;
            }
        }
        return query;
    }
    replaceVariablesInScriptQuery(query, variables) {
        // Reverse sort to ensure correct variable replacement
        const sortedVariables = variables
            .slice()
            .sort((a, b) => (a.name < b.name ? 1 : b.name < a.name ? -1 : 0));
        for (const variable of sortedVariables) {
            const value = formatVariableValue(variable);
            query = query.replace(RegExp(`@${variable.name}`, 'g'), value);
        }
        return query;
    }
    /**
     * Returns the SQL corresponding to the passed workflow wrapped in a stored procedure
     *
     * @param workflow: workflow object
     * @param envVars: workflow environment variables
     * @param isImmutable: whether the workflow is inmutable or not.
     * If so, the cached result table will be used if exists
     * @param includeDiagram: whether to include the workflow diagram as a comment
     * @param schemas (optional): workflow schemas
     */
    async getAsStoredProcedure(workflow, envVars, isImmutable, includeDiagram = true, schemas) {
        const name = workflow.getStoredProcedureName();
        let body = await this.getWorkflowCode(workflow, envVars, WorkflowCodeType.StoredProcedure, schemas);
        const workflowOutputSourceTableName = workflow.getStoredProcedureOutputSourceTableName(this);
        let select = '';
        if (workflowOutputSourceTableName || isImmutable) {
            if (workflowOutputSourceTableName) {
                select =
                    'SELECT * FROM ' + workflowOutputSourceTableName.split('.').pop();
            }
            else {
                select = 'SELECT 1 as dummy';
            }
            const createTempTable = this.getCreateTempTableQuery('##TABLENAME##', select, WorkflowCodeType.FullNoCache, null);
            const queries = createTempTable.split(';').filter((s) => /\S/.test(s));
            let dynamicSQL = '';
            for (const query of queries) {
                dynamicSQL = dedent `${dynamicSQL}
          ${this.executeDynamicSQL({
                    sql: query,
                    addQuotes: true,
                    replacements: [["'##TABLENAME##'", OUTPUT_TABLE_FQN_VAR_NAME]]
                })}
          `;
            }
            body = dedent `${body}
            ${dynamicSQL}
            `;
        }
        if (isImmutable) {
            body = dedent `
            ${this.wrapCodeInIfTableExistsBlock(body, [], WorkflowCodeType.FullNoCache, // If we pass WorkflowCodeType.StoredProcedure, it will not wrap the code, but this is a particular case, since it is not to wrap the code of a component, but the code of the whole workflow
            [OUTPUT_TABLE_NAME_VAR_NAME], 'procedure')}
      `;
        }
        else if (workflowOutputSourceTableName) {
            body = dedent `
                ${this.executeDynamicSQL({
                sql: `DROP TABLE IF EXISTS ${this.quoteName('##TABLENAME##')}`,
                addQuotes: true,
                replacements: [["'##TABLENAME##'", OUTPUT_TABLE_FQN_VAR_NAME]]
            })}
                ${body}
                `;
        }
        const procedure = this.wrapCodeInProcedure(name, workflow, body, includeDiagram, isImmutable);
        return procedure;
    }
    /**
     * Returns a list of tasks defining the workflow. This can be used to recreate the workflow
     * in the data warehouse, using its native implementation of workflows
     * @param workflow the workflow to get the tasks for
     * @param envVars the environment variables for running this workflow
     * @param isImmutable whether the workflow is immutable or not.
     * If so, the cached result table will be used if exists
     * @param schemas the schemas of the tables in the workflow
     * @returns A list of tasks defining the workflow. Null if it doesn't apply to the provider.
     */
    async getAsTasksList(workflow, envVars, isImmutable, schemas) {
        return null;
    }
    /**
     * Returns the comment to embed in the stored procedure, containing the workflow description.
     * That includes the json diagram to recreate the workflow, along with some additional
     * properties.
     * @param workflow The workflow to describe
     * @param isImmutable Whether the stored procedure is inmutable or not
     * @returns the comment to embed in the stored procedure
     */
    _getStoredProcedureEmbeddedComment(workflow, isImmutable) {
        let diagramJson = workflow.diagram;
        if (diagramJson) {
            diagramJson = (({ executionInfo, ...o }) => o)(workflow.diagram);
        }
        const diagramMetadata = {
            versionId: workflow.workflowVersionHash(),
            paramsId: workflow.workflowParamsHash(),
            isImmutable,
            diagramJson: JSON.stringify(diagramJson)
        };
        return dedent `/*
                   ${JSON.stringify(diagramMetadata)}
                  */`;
    }
    _getSourceTables(workflow) {
        const tables = [];
        for (const [componentName, component] of Object.entries(workflow.components)) {
            if (component.metadata.name === new ReadTable().metadata.name) {
                const value = workflow.componentValues[componentName]['source'];
                const tablename = value.value;
                if (!tables.includes(tablename)) {
                    tables.push(tablename);
                }
            }
        }
        return tables;
    }
    getStoredProcedureFullName(workflow) {
        return this.quoteName(`${this.tempStoragePath}.${workflow.getStoredProcedureName()}`);
    }
    getRemoveStoredProcedureQuery(workflow) {
        const procedureParams = workflow.variables
            .filter((v) => v.public)
            .map((input) => `${this.paramTypeToDataProviderType(input.type, input.value)}`);
        return dedent `DROP PROCEDURE IF EXISTS ${this.getStoredProcedureFullName(workflow)}(${procedureParams.join()})`;
    }
    /**
     * Returns a query that can be executed in the data warehouse to get the row counts of the tables generated
     * by a given component in the workflow.
     * @param workflow The workflow whose table counts are to be obtained.
     * @param componentName The name of the component whose table counts are to be obtained.
     * @param params The parameters used to run the component
     * Returns null in case the provider does not require to run an extra query to get the row counts.
     */
    getOutputTableCountsQuery(workflow, componentName, params) {
        const outputTables = workflow.getOutputTables(this, WorkflowCodeType.Full);
        const componentOutputTables = outputTables[componentName];
        const tablenames = [];
        const tablenamesNotCreated = [];
        for (const [output, tablename] of Object.entries(componentOutputTables)) {
            const isCreated = workflow.components[componentName].isOutputTableCreated(workflow, componentName, output, params);
            if (isCreated) {
                tablenames.push(tablename);
            }
            else {
                tablenamesNotCreated.push(tablename);
            }
        }
        return this._getTableCountsQuery(tablenames, tablenamesNotCreated);
    }
    _getTableCountsQuery(tablenames, tablenamesNotCreated) {
        let countQueries = [];
        for (const tablename of tablenames) {
            countQueries.push(dedent `
          SELECT
            COUNT(*) AS rowCount,
            '${this.applyCapitalization(tablename)}' AS fqn
          FROM ${this.quoteName(this.applyCapitalization(tablename))}`);
        }
        for (const tablename of tablenamesNotCreated) {
            countQueries.push(dedent `
          SELECT
            0 AS rowCount,
            '${this.applyCapitalization(tablename)}' AS fqn`);
        }
        return countQueries.join('\nUNION ALL\n');
    }
    /**
     * Returns a the expected name for an output table generated by a given component in a workflow, with a given set
     * of input parameters
     * @param workflowId The id of the workflow
     * @param componentName The name of the component (the 'name' property of the component class)
     * @param params the params used to run the component
     * @param output the name of the output parameter
     * @param codeType the type of code to produce
     * @returns The table name
     */
    getOutputTableName(workflowId, componentName, params, output, codeType) {
        const toRemove = [OPTIMIZATION_COLUMN_PARAMETER_NAME];
        // these params do not affect the content of the table, so we remove them for computing the hash.
        const paramsNoClustering = Object.keys(params)
            .filter((key) => !toRemove.includes(key))
            .reduce((result, key) => ({ ...result, [key]: params[key] }), {});
        const s = JSON.stringify({ componentName, params: paramsNoClustering });
        let table = `WORKFLOW_${hashString(workflowId)}_${hashString(s)}_${output}`;
        if (codeType === WorkflowCodeType.DryRun) {
            table += DRY_RUN_SUFFIX;
        }
        if (codeType !== WorkflowCodeType.StoredProcedure) {
            table = `${this.tempStoragePath}.${table}`;
        }
        return table;
    }
    getOutputViewName(workflowId, componentName, params, output, codeType) {
        if (codeType === WorkflowCodeType.StoredProcedure) {
            codeType = WorkflowCodeType.Full;
        }
        return this.getOutputTableName(workflowId, componentName, params, output, codeType);
    }
    getTempStorageCleanUpQuery() {
        return null;
    }
    quoteName(name) {
        return name;
    }
    unquoteName(name) {
        return name.replace(/["`]/g, '');
    }
    quoteMultilineString(str) {
        return dedent `'${str}'`;
    }
    quoteVariableName(name) {
        return `"${name}"`;
    }
    processInputGeogColumn(name) {
        return name;
    }
    processOutputGeogColumn(name) {
        return name;
    }
    executeDynamicSQL({ sql, addQuotes, replacements }) {
        const lines = sql
            .replace(/BEGIN\n([\s\S]*?)END;/, '$1')
            .split(';')
            .filter((s) => /\S/.test(s));
        if (replacements) {
            return lines
                .map((q) => dedent `EXECUTE IMMEDIATE
                          ${dedent(replacements?.reduce((acc, [org, repl]) => dedent `REPLACE(
                                ${acc},
                                ${org},
                                ${repl}
                              )
                              `, addQuotes
                ? this.quoteMultilineString(dedent(q))
                : dedent(q)))};`)
                .join('\n');
        }
        else {
            return lines
                .map((q) => dedent `EXECUTE IMMEDIATE
                          ${addQuotes
                ? this.quoteMultilineString(dedent(q))
                : dedent(q)}
                        ;`)
                .join('\n');
        }
    }
    getStoredProceduresQuery() {
        return null;
    }
    getTableListQuery(filter) {
        return null;
    }
    getStoredProcedureComponentsFromQueryResponse(defs) {
        const validComponents = [];
        const errors = [];
        for (const definition of defs) {
            try {
                validComponents.push(new StoredProcedureWorkflowComponent(definition));
            }
            catch (e) {
                errors.push({
                    storedProcedureCode: definition,
                    error: e.toString()
                });
            }
        }
        return {
            validComponents,
            errors
        };
    }
    /**
     * Returns the SQL code to uninstall an extension from its connection
     * @param name The name of the extension to uninstall
     * @returns
     */
    getExtensionUninstallQuery(name) {
        return null;
    }
    /**
     * Returns the SQL code to get information about the extensions and components
     * installed in this connection. Once the query is executed, the response
     * should be passed to the getExtensionComponentsFromQueryResponse function.
     * @returns the SQL code to get information about the extensions and components
     * installed in this connection
     */
    getExtensionComponentsQuery() {
        const extensionsTable = dedent `${this.quoteName(this.tempStoragePath)}.${EXTENSIONS_TABLE_NAME}`;
        return dedent `
          SELECT
            name, metadata
          FROM
            ${extensionsTable}
          `;
    }
    /**
     * Given the response obtained from the data warehouse when running the query
     * returned by the getExtensionComponentsQuery function, it parses it and returns
     * the extensions and components installed in the connection.
     * @param extensions The response obtained from running the query returned by the
     * getExtensionComponentsQuery function.
     * @returns An object with the components installed in the connection.
     */
    getExtensionComponentsFromQueryResponse(response) {
        const validComponents = [];
        const errors = [];
        for (const row of response) {
            try {
                const extension = JSON.parse(row.metadata);
                for (const componentDefinition of extension.components) {
                    try {
                        validComponents.push(new ExtensionWorkflowComponent(componentDefinition));
                    }
                    catch (e) {
                        errors.push({
                            extensionName: extension.name,
                            componentName: componentDefinition.name,
                            error: e.toString()
                        });
                    }
                }
            }
            catch (e) {
                errors.push({
                    extensionName: row.name,
                    componentName: null,
                    error: e.toString()
                });
            }
        }
        return {
            validComponents,
            errors
        };
    }
    applyCapitalization(tablename) {
        return tablename;
    }
    adaptComponent(component) {
        // By default, do nothing
    }
    wrapSQLCodeAs(query) {
        return query;
    }
    createTableFromSchema(schema, tableName) {
        const columns = schema
            .map((col) => {
            const type = col.db_type || this.columnTypeToDataProviderType(col.type);
            return `${this.quoteVariableName(col.name)} ${type}`;
        })
            .join(', ');
        const query = dedent `
      CREATE TABLE ${this.quoteName(tableName)} (${columns});`;
        return this.wrapCodeInIfTableExistsBlock(this.wrapSQLCodeAs(query), [tableName], WorkflowCodeType.Full);
    }
    /**
     * Returns the query to get the schemas of the output tables of the workflow
     * @param tableNames An array with the names of the tables to get the schemas for
     * These names should be table names (not FQNs) of the tables, in dryrun mode.
     * They are assumed to be in the temp storage path.
     * @returns  The query to get the schemas of the passed output tables
     */
    getOutputSchemasQueries(tableNames) {
        return '';
    }
    /**
     * Takes a series of SQL statements and prepares them to be executed in the provider,
     * in order to get the schemas from the last one of them
     * @param queries The queries to prepare
     */
    prepareSchemaQueries(queries) {
        return [`BEGIN\n${queries.join('\n')}\nEND;`];
    }
    /**
     * Returns the data type used for new geography columns
     * @returns the data type used for new geography columns
     */
    getGeoDataType() {
        return ColumnDataType.Geography;
    }
}
